import { daysBetween, plural, TODAY } from '@/lib/format'
import { supplierCategoryLabels, supplierStatusLabels } from '@/lib/labels'
import type { Supplier, WeddingData } from '@/mock/types'
import type { LinkedModule } from '@/patterns/linked-record'
import { taskPromptState } from '@/patterns/prompts'
import { scheduleSignature } from '../schedule/model'

export const supplierFilters = ['all', 'inquiry', 'agreed', 'booked', 'cancelled'] as const
export type SupplierFilter = (typeof supplierFilters)[number]

export const filterLabels: Record<SupplierFilter, string> = {
  all: 'Vše',
  ...supplierStatusLabels,
}

export function parseFilter(value: string | undefined): SupplierFilter {
  return supplierFilters.find((filter) => filter === value) ?? 'all'
}

export function matchesFilter(supplier: Supplier, filter: SupplierFilter) {
  return filter === 'all' || supplier.status === filter
}

export function matchesSearch(supplier: Supplier, query: string) {
  const needle = query.trim().toLocaleLowerCase('cs')
  if (!needle) return true
  return [
    supplier.name,
    supplierCategoryLabels[supplier.category] ?? supplier.category,
    ...supplier.contacts.map((contact) => contact.name),
  ].some((value) => value.toLocaleLowerCase('cs').includes(needle))
}

/** Domluveno: the couple has agreed with the supplier, signed or not. */
export const isAgreed = (supplier: Supplier) =>
  supplier.status === 'agreed' || supplier.status === 'booked'

/** The order categories appear in, roughly the order couples book them. */
export const categoryOrder = [
  'venue',
  'catering',
  'officiant',
  'photographer',
  'videographer',
  'party-music',
  'ceremony-music',
  'moderator',
  'florist',
  'decor',
  'cake-sweets',
  'bar',
  'hair',
  'makeup',
  'bride-attire',
  'groom-attire',
  'rings',
  'stationery',
  'rentals',
  'transport',
  'accommodation',
  'coordination',
  'entertainment',
  'other',
]

export const categoryLabel = (category: string) => supplierCategoryLabels[category] ?? category

/** The planner section a supplier of this category is picked in. */
const categorySections: Record<string, LinkedModule> = {
  venue: 'venue',
  officiant: 'ceremony',
  catering: 'reception',
  bar: 'reception',
  'cake-sweets': 'reception',
  photographer: 'photo',
  videographer: 'photo',
  'party-music': 'music',
  'ceremony-music': 'music',
  moderator: 'music',
  entertainment: 'music',
  hair: 'beauty',
  makeup: 'beauty',
  florist: 'decor',
  decor: 'decor',
}

export const sectionOf = (category: string): LinkedModule | undefined => categorySections[category]

export type SupplierUsage = { module: LinkedModule; label: string }

/** Where the supplier shows up elsewhere in the plan ("Kde figuruje"). */
export function supplierUsage(supplier: Supplier, data: WeddingData): SupplierUsage[] {
  const usage: SupplierUsage[] = []
  const section = categorySections[supplier.category]
  if (section && isAgreed(supplier)) usage.push({ module: section, label: 'Na stránce sekce' })
  const events = data.schedule.filter(
    (event) => !event.removed && event.owner?.type === 'supplier' && event.owner.id === supplier.id,
  ).length
  if (events > 0)
    usage.push({
      module: 'schedule',
      label: `Harmonogram: ${events} ${plural(events, 'položka', 'položky', 'položek')}`,
    })
  if (data.budget.expenses.some((expense) => expense.supplierId === supplier.id))
    usage.push({ module: 'budget', label: 'Výdaj v rozpočtu' })
  return usage
}

export type SupplierPromptAction =
  | { kind: 'task'; title: string }
  | { kind: 'catalog'; category: string }
  | { kind: 'budget' }
  | { kind: 'schedule' }
  | { kind: 'open'; supplierId: string }

export type SupplierPrompt = {
  code: string
  title: string
  hint: string
  action: SupplierPromptAction
  state: 'open' | 'in-tasks'
}

/** The categories nearly every wedding needs, and the question to ask when one is missing. */
const keyCategories: [string, string][] = [
  ['venue', 'Kde bude svatba?'],
  ['catering', 'Kdo bude vařit?'],
  ['officiant', 'Kdo vás oddá?'],
  ['photographer', 'Kdo vás bude fotit?'],
  ['party-music', 'Kdo bude hrát na hostině?'],
]

const OFFER_WAIT_DAYS = 14

/**
 * "Na co nezapomenout" for Moji dodavatelé: missing key suppliers, a cancelled
 * one with no replacement, contracts and prices still missing, a schedule the
 * suppliers haven't got, offers that take too long and suppliers nobody can call.
 */
export function supplierPrompts(data: WeddingData): SupplierPrompt[] {
  const suppliers = data.suppliers
  const active = suppliers.filter((supplier) => supplier.status !== 'cancelled')
  const agreed = suppliers.filter(isAgreed)
  const prompts: Omit<SupplierPrompt, 'state'>[] = []

  for (const [category, title] of keyCategories)
    if (!suppliers.some((supplier) => supplier.category === category))
      prompts.push({
        code: `suppliers-missing-${category}`,
        title,
        hint: `Zatím nemáte nikoho v kategorii ${categoryLabel(category)}.`,
        action: { kind: 'catalog', category },
      })

  const cancelledOnly = [...new Set(suppliers.map((supplier) => supplier.category))].filter(
    (category) =>
      suppliers.some((supplier) => supplier.category === category) &&
      !active.some((supplier) => supplier.category === category),
  )
  for (const category of cancelledOnly) {
    const cancelled = suppliers.filter((supplier) => supplier.category === category)
    prompts.push({
      code: `suppliers-replace-${category}`,
      title: `Kdo nahradí ${cancelled.length === 1 && cancelled[0] ? cancelled[0].name : categoryLabel(category).toLowerCase()}?`,
      hint: `V kategorii ${categoryLabel(category)} je jen zrušený dodavatel.`,
      action: { kind: 'catalog', category },
    })
  }

  const unsigned = suppliers.filter((item) => item.status === 'agreed')
  if (unsigned.length === 1 && unsigned[0])
    prompts.push({
      code: `suppliers-contract-${unsigned[0].id}`,
      title: `Máte smlouvu s dodavatelem ${unsigned[0].name}?`,
      hint: 'Je domluvený, ale smlouva zatím chybí.',
      action: { kind: 'task', title: `Podepsat smlouvu: ${unsigned[0].name}` },
    })
  else if (unsigned.length > 1)
    prompts.push({
      code: 'suppliers-contracts',
      title: 'Máte smlouvy s domluvenými dodavateli?',
      hint: `${names(unsigned)} ${plural(unsigned.length, 'je domluvený', 'jsou domluvení', 'je domluvených')}, ale smlouva chybí.`,
      action: { kind: 'task', title: 'Podepsat smlouvy s domluvenými dodavateli' },
    })

  const unpriced = agreed.filter((supplier) => {
    const expense = data.budget.expenses.find((item) => item.supplierId === supplier.id)
    return !expense || expense.amount === 0
  })
  if (unpriced.length > 0)
    prompts.push({
      code: 'suppliers-price',
      title:
        unpriced.length === 1 && unpriced[0]
          ? `Kolik stojí ${unpriced[0].name}?`
          : 'Kolik stojí domluvení dodavatelé?',
      hint: `${names(unpriced)} ${plural(unpriced.length, 'nemá', 'nemají', 'nemá')} v rozpočtu cenu.`,
      action: { kind: 'budget' },
    })

  const signature = scheduleSignature(data.schedule)
  const withEvents = agreed.filter((supplier) =>
    data.schedule.some(
      (event) =>
        !event.removed && event.owner?.type === 'supplier' && event.owner.id === supplier.id,
    ),
  )
  const notSent = withEvents.filter(
    (supplier) => !data.scheduleShares.some((share) => share.supplierId === supplier.id),
  )
  const outdated = withEvents.filter((supplier) =>
    data.scheduleShares.some(
      (share) => share.supplierId === supplier.id && share.signature !== signature,
    ),
  )
  if (notSent.length + outdated.length > 0)
    prompts.push({
      code: 'suppliers-schedule',
      title: 'Mají dodavatelé aktuální harmonogram?',
      hint: [
        notSent.length > 0 &&
          `${names(notSent)} ${plural(notSent.length, 'ho ještě nedostal', 'ho ještě nedostali', 'ho ještě nedostalo')}`,
        outdated.length > 0 &&
          `${names(outdated)} ${plural(outdated.length, 'má', 'mají', 'má')} starší verzi`,
      ]
        .filter(Boolean)
        .join(', ')
        .concat('.'),
      action: { kind: 'schedule' },
    })

  for (const supplier of suppliers.filter(
    (item) =>
      item.status === 'inquiry' &&
      item.statusChangedAt &&
      daysBetween(item.statusChangedAt, TODAY) > OFFER_WAIT_DAYS,
  )) {
    const days = daysBetween(supplier.statusChangedAt as string, TODAY)
    prompts.push({
      code: `suppliers-offer-${supplier.id}`,
      title: `Ozval se ${supplier.name}?`,
      hint: `Poptávka běží ${days} ${plural(days, 'den', 'dny', 'dní')}.`,
      action: { kind: 'task', title: `Připomenout nabídku: ${supplier.name}` },
    })
  }

  const noContact = active.filter((supplier) => supplier.contacts.length === 0)
  if (noContact[0])
    prompts.push({
      code: 'suppliers-contacts',
      title: 'Komu zavoláte?',
      hint: `${names(noContact)} ${plural(noContact.length, 'nemá', 'nemají', 'nemá')} kontaktní osobu.`,
      action: { kind: 'open', supplierId: noContact[0].id },
    })

  return prompts.flatMap((prompt) => {
    if (data.hiddenPrompts.includes(prompt.code)) return []
    if (prompt.action.kind !== 'task') return [{ ...prompt, state: 'open' as const }]
    const state = taskPromptState(data.tasks, prompt.action.title)
    return state === 'done' ? [] : [{ ...prompt, state }]
  })
}

function names(suppliers: Supplier[]) {
  const list = suppliers.map((supplier) => supplier.name)
  if (list.length <= 2) return list.join(' a ')
  return `${list.slice(0, 2).join(', ')} a ${list.length - 2} ${plural(list.length - 2, 'další', 'další', 'dalších')}`
}
